import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { Lightning, X, FireSimple } from '@phosphor-icons/react';

const STORAGE_KEY = 'bx_streak_shown_';

/**
 * Daily-return streak modal. Mounts inside the AppShell so it pops on any first
 * authenticated page-view of the day. Stores a date marker in localStorage so
 * it only triggers once per local day.
 */
const StreakModal = () => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState({ streak: 0, bonus_xp: 0, already: false });

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const last = localStorage.getItem(STORAGE_KEY + 'date');
    if (last === today) return;

    api.post('/streak/checkin').then(({ data }) => {
      localStorage.setItem(STORAGE_KEY + 'date', today);
      if (!data.already && data.bonus_xp > 0) {
        setData(data);
        toast(`Day ${data.streak} together · +${data.bonus_xp} XP`, { duration: 3500 });
      }
    }).catch(() => {});
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4"
          onClick={() => setShow(false)}
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 18 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="streak-modal"
            className="grad-border p-8 sm:p-10 max-w-sm w-full text-center relative overflow-hidden"
          >
            <button onClick={() => setShow(false)} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="streak-close">
              <X size={18} weight="bold" />
            </button>
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(14)].map((_, i) => (
                <motion.span
                  key={i}
                  className="absolute h-1.5 w-1.5 rounded-full"
                  style={{ background: i % 2 ? '#ccff00' : '#ff007f', top: '50%', left: '50%' }}
                  initial={{ opacity: 0 }}
                  animate={{
                    x: (Math.random() - 0.5) * 220,
                    y: (Math.random() - 0.5) * 280,
                    opacity: [0, 1, 0],
                  }}
                  transition={{ duration: 2.4, delay: 0.2 + i * 0.05, repeat: Infinity, repeatDelay: 2 }}
                />
              ))}
            </div>
            <div className="relative">
              <div className="h-24 w-24 mx-auto rounded-full bg-gradient-to-br from-bx-legendary via-bx-magenta to-bx-cyan grid place-items-center breathe">
                <FireSimple size={40} weight="fill" className="text-white" />
              </div>
              <motion.div
                initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.15 }}
                className="font-display text-6xl font-black mt-5 shimmer-text"
              >
                {data.streak} <span className="text-2xl text-white/80">{data.streak === 1 ? 'day' : 'days'}</span>
              </motion.div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/65 mt-1">current streak</div>
              <div className="mt-6 chip text-bx-volt border-bx-volt/30">
                <Lightning size={12} weight="fill" /> +{data.bonus_xp} XP
              </div>
              <p className="text-white/65 mt-4 text-sm">
                {data.streak === 1 ? "Day one. The movement starts now." :
                 data.streak < 7 ? "Keep the streak alive — bigger rewards every day." :
                 data.streak < 30 ? "Relentless. You're rising on the leaderboard." :
                 "Legendary. You're shaping the network."}
              </p>
              <button
                onClick={() => setShow(false)}
                data-testid="streak-continue"
                className="btn-primary px-6 py-3 rounded-full text-sm mt-6"
              >
                Let's go
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StreakModal;
